/**
 * The drawn pieces, in the hero animation's own ink: its colour (#4B2907), its
 * line weight, and its line boil. `useBoil` re-seeds every `feTurbulence.boil`
 * ten times a second, which is what makes a line drawn in SVG look drawn by
 * hand rather than plotted.
 */

/** The handset, shared by every drawn button. Lucide's phone, at its own scale. */
const HANDSET =
  "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z";

/**
 * The page's one copy of the boil filter, off-screen. The floating pill carries
 * its own (`ink-sm`, displaced harder) because it is drawn much smaller and the
 * wobble has to survive the scale.
 */
export function InkDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        <filter id="ink" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence
            className="boil"
            type="fractalNoise"
            baseFrequency="0.03"
            numOctaves="1"
            seed="0"
          />
          <feDisplacementMap
            in="SourceGraphic"
            scale="2.2"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
        {/* For icons drawn on a 24-unit grid: the same boil, its noise finer
            so it wobbles the line rather than shifting the whole icon. */}
        <filter id="ink-xs" x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence
            className="boil"
            type="fractalNoise"
            baseFrequency="0.3"
            numOctaves="1"
            seed="0"
          />
          <feDisplacementMap
            in="SourceGraphic"
            scale="1.6"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>
  );
}

/** The green disc with the handset on it, and the arcs that appear when it rings. */
function Face({ withDots }: { withDots?: boolean }) {
  return (
    <g className="face">
      <circle cx="52" cy="32" r="26" stroke="#4B2907" strokeWidth="2.8" />
      <g transform="translate(40.5 20.5) scale(0.96)">
        <path
          className="glyph"
          fill="#fff"
          stroke="#fff"
          strokeWidth="1.2"
          strokeLinejoin="round"
          d={HANDSET}
        />
      </g>
      {withDots && (
        <g className="dots" fill="#fff">
          <circle cx="41" cy="33" r="3.4" />
          <circle cx="52" cy="33" r="3.4" />
          <circle cx="63" cy="33" r="3.4" />
        </g>
      )}
    </g>
  );
}

/**
 * The Call it live drawing: the disc, the three connecting dots behind the
 * handset, and the ring arcs either side.
 */
export function InkedPhone() {
  return (
    <svg className="inked" width="104" height="64" viewBox="0 0 104 64" aria-hidden="true">
      <g filter="url(#ink)">
        <g className="arcs" fill="none" stroke="#4B2907" strokeWidth="2.8" strokeLinecap="round">
          <path d="M 18 22 Q 13 32 18 42" />
          <path className="outer" d="M 10 16 Q 2 32 10 48" />
          <path d="M 86 22 Q 91 32 86 42" />
          <path className="outer" d="M 94 16 Q 102 32 94 48" />
        </g>
        <Face withDots />
      </g>
    </svg>
  );
}

/** The same drawing at 36px, cropped to its circle, for the floating pill. */
export function InkedPhoneSmall() {
  return (
    <svg className="inked" viewBox="24 4 56 56" aria-hidden="true">
      <defs>
        <filter id="ink-sm" x="-10%" y="-10%" width="120%" height="120%">
          <feTurbulence
            className="boil"
            type="fractalNoise"
            baseFrequency="0.03"
            numOctaves="1"
            seed="0"
          />
          <feDisplacementMap
            in="SourceGraphic"
            scale="3.2"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
      <g filter="url(#ink-sm)">
        <Face />
      </g>
    </svg>
  );
}

/** The handset alone, for the hero's Try it live pill. It rings on its own. */
export function RingingHandset() {
  return (
    <svg className="ph" viewBox="0 0 24 24" aria-hidden="true">
      <g>
        <path
          className="handset"
          fill="#4B2907"
          stroke="#4B2907"
          strokeWidth="1.2"
          strokeLinejoin="round"
          d={HANDSET}
        />
      </g>
    </svg>
  );
}

/**
 * A pen stroke under a few words of a heading, drawn on once and boiling
 * after, in the call button's ink.
 */
export function Scribble({ children }: { children: React.ReactNode }) {
  return (
    <span className="scrib">
      {children}
      <svg viewBox="0 0 400 16" aria-hidden="true">
        <defs>
          {/* Its own region, in the drawing's units: a stroke this thin has
              almost no box, and the boil was clipped at its edges. */}
          <filter id="ink-line" filterUnits="userSpaceOnUse" x="-20" y="-20" width="440" height="64">
            <feTurbulence className="boil" type="fractalNoise" baseFrequency="0.03" numOctaves="1" seed="0" />
            <feDisplacementMap in="SourceGraphic" scale="3" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
        <path
          filter="url(#ink-line)"
          d="M 6 9 C 100 4, 230 3, 394 5 C 290 7, 170 9, 70 12"
          fill="none"
          stroke="#4B2907"
          strokeWidth="5"
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength={1}
        />
      </svg>
    </span>
  );
}
